import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload,
  Trash2,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  TrendingUp,
  TrendingDown,
  Briefcase,
  AlertCircle,
  Camera,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import Navbar from "../components/Navbar";
import SignalDetailModal from "../components/SignalDetailModal";
import DailyPicksWidget from "../components/DailyPicksWidget";
import { api } from "../lib/api";
import {
  fmtMoney,
  fmtPct,
  marginColor,
  displayTicker,
} from "../lib/format";
import { Button } from "../components/ui/button";

const SIGNAL_STYLE = {
  BUY:        { bg: "bg-moss text-bone-50 border-moss",                              emoji: "🟢" },
  ACCUMULATE: { bg: "bg-moss-bg text-moss-light border-moss-light/40",               emoji: "🟡" },
  HOLD:       { bg: "bg-bone-100 text-stone_brand-dark border-line",                 emoji: "⚪" },
  SELL:       { bg: "bg-terracotta text-bone-50 border-terracotta",                  emoji: "🔴" },
  NO_DATA:    { bg: "bg-bone-100 text-stone_brand-light border-line/60 italic",      emoji: "—" },
};

function SignalPill({ rec, onClick }) {
  if (!rec) return null;
  const s = SIGNAL_STYLE[rec.signal] || SIGNAL_STYLE.NO_DATA;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className={`inline-flex flex-col items-start border rounded-sm px-2 py-1 cursor-pointer hover:opacity-90 hover:scale-[1.02] active:scale-[0.98] transition-all ${s.bg}`}
      title="Click to see the math"
      data-testid={`portfolio-signal-${rec.signal}`}
    >
      <span className="text-[0.6rem] font-mono uppercase tracking-widest leading-tight font-semibold whitespace-nowrap">
        {s.emoji} {rec.label}
      </span>
      {rec.upside_2030_pct !== null && rec.upside_2030_pct !== undefined && (
        <span className="text-[0.55rem] font-mono opacity-75 leading-tight">
          2030 +{rec.upside_2030_pct}%
        </span>
      )}
    </button>
  );
}

export default function Portfolio() {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ holdings: [], total_invested: 0, total_current: 0, total_pnl: 0 });
  const [sortBy, setSortBy] = useState("pnl_pct");
  const [sortDir, setSortDir] = useState("desc");
  const [filterSignal, setFilterSignal] = useState("ALL");
  const [search, setSearch] = useState("");
  const [unmatched, setUnmatched] = useState([]);
  const [detailHolding, setDetailHolding] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/portfolio", { timeout: 180000 });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load portfolio");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleUpload = async (e, replace = false) => {
    const f = e.target.files?.[0];
    if (!f || uploading) return;
    setUploading(true);
    setUnmatched([]);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: r } = await api.post(`/portfolio/upload?replace=${replace}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success(`${r.added} holdings imported${replace ? " (replaced)" : ""}`);
      setUnmatched(r.unmatched || []);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const { data } = await api.get("/portfolio", { timeout: 180000 });
      setData(data);
      toast.success("Prices refreshed");
    } catch {
      toast.error("Could not refresh prices");
    } finally {
      setRefreshing(false);
    }
  };

  const handlePhotoScan = async (e) => {
    const f = e.target.files?.[0];
    if (!f || scanning) return;
    setScanning(true);
    setUnmatched([]);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: r } = await api.post("/portfolio/upload-image?replace=false", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success(`${r.added} holdings scanned from photo`);
      setUnmatched(r.unmatched || []);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo scan failed");
    } finally {
      setScanning(false);
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const handleRemove = async (ticker) => {
    try {
      await api.delete(`/portfolio/${encodeURIComponent(ticker)}`);
      setData((d) => ({ ...d, holdings: (d.holdings || []).filter((h) => h.ticker !== ticker) }));
      toast.success(`${displayTicker(ticker)} removed`);
    } catch {
      toast.error("Could not remove holding");
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Wipe all holdings? You can re-upload anytime.")) return;
    try {
      await api.delete("/portfolio");
      setData({ holdings: [], total_invested: 0, total_current: 0, total_pnl: 0 });
      toast.success("Portfolio cleared");
    } catch {
      toast.error("Could not clear");
    }
  };

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  const counts = useMemo(() => {
    const c = { BUY: 0, ACCUMULATE: 0, HOLD: 0, SELL: 0 };
    (data.holdings || []).forEach((h) => {
      const sig = (h.recommendation || {}).signal;
      if (c[sig] !== undefined) c[sig] += 1;
    });
    return c;
  }, [data.holdings]);

  const rows = useMemo(() => {
    let arr = (data.holdings || []).map((h) => ({
      ...h,
      _signal: (h.recommendation || {}).signal || "NO_DATA",
    }));
    if (filterSignal !== "ALL") arr = arr.filter((h) => h._signal === filterSignal);
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter(
        (h) =>
          (h.ticker || "").toLowerCase().includes(q) ||
          (h.company_name || "").toLowerCase().includes(q) ||
          (h.name || "").toLowerCase().includes(q),
      );
    }
    const dir = sortDir === "asc" ? 1 : -1;
    return arr.sort((a, b) => {
      const va = a[sortBy];
      const vb = b[sortBy];
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      if (typeof va === "string") return va.localeCompare(vb) * dir;
      return (va - vb) * dir;
    });
  }, [data.holdings, sortBy, sortDir, filterSignal, search]);

  const pnlPct = data.total_pnl_pct;

  return (
    <div className="min-h-screen bg-bone-50" data-testid="portfolio-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        {/* Header */}
        <div className="mb-10">
          <div className="overline mb-3">— Demat Holdings · INDmoney</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-stone_brand-dark tracking-tight font-semibold">
            My Stocks
          </h1>
          <p className="text-stone_brand mt-2 max-w-2xl">
            Upload your INDmoney holdings export to see live P&amp;L next to{" "}
            <span className="font-mono text-moss">Morningstar fair value</span> and{" "}
            <span className="font-mono text-moss">2030 target</span> — with Buy / Accumulate / Hold / Sell signals computed deterministically from your worksheet.
          </p>
        </div>

        {/* Summary tiles */}
        {data.holdings && data.holdings.length > 0 && (
          <DailyPicksWidget scope="portfolio" limit={6} />
        )}

        {/* Summary tiles */}
        {data.holdings && data.holdings.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <div className="bg-white border border-line rounded-sm p-5">
              <div className="overline mb-1.5">Invested</div>
              <div className="font-mono text-2xl text-stone_brand-dark">{fmtMoney(data.total_invested, "INR", 0)}</div>
            </div>
            <div className="bg-white border border-line rounded-sm p-5">
              <div className="overline mb-1.5">Current Value</div>
              <div className="font-mono text-2xl text-stone_brand-dark">{fmtMoney(data.total_current, "INR", 0)}</div>
            </div>
            <div className={`border rounded-sm p-5 ${data.total_pnl >= 0 ? "bg-moss-bg border-moss-light/30" : "bg-terracotta-bg border-terracotta/30"}`}>
              <div className="overline mb-1.5">Realised P&amp;L</div>
              <div className={`font-mono text-2xl flex items-center gap-2 ${data.total_pnl >= 0 ? "text-moss-light" : "text-terracotta"}`}>
                {data.total_pnl >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                {fmtMoney(data.total_pnl, "INR", 0)}
              </div>
              {pnlPct !== null && pnlPct !== undefined && (
                <div className={`font-mono text-xs mt-0.5 ${data.total_pnl >= 0 ? "text-moss-light" : "text-terracotta"}`}>
                  {fmtPct(pnlPct)}
                </div>
              )}
            </div>
            <div className="bg-white border border-line rounded-sm p-5">
              <div className="overline mb-1.5">Holdings</div>
              <div className="font-mono text-2xl text-stone_brand-dark">{data.holdings.length}</div>
            </div>
          </div>
        )}

        {/* Upload + signal filter row */}
        <div className="bg-white border border-line rounded-sm p-5 mb-8 flex flex-wrap items-center gap-4">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => handleUpload(e, true)}
            data-testid="portfolio-file-input"
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handlePhotoScan}
            data-testid="portfolio-camera-input"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading || scanning}
            className="rounded-sm bg-moss hover:bg-moss-hover text-bone-50 h-11 px-5 gap-2"
            data-testid="portfolio-upload-btn"
          >
            <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : data.holdings?.length > 0 ? "Upload New File" : "Upload INDmoney XLSX"}
          </Button>
          <Button
            onClick={() => cameraRef.current?.click()}
            disabled={uploading || scanning}
            variant="outline"
            className="rounded-sm border-moss text-moss hover:bg-moss-bg h-11 px-5 gap-2"
            data-testid="portfolio-scan-photo-btn"
          >
            <Camera className="w-4 h-4" /> {scanning ? "Reading photo…" : "Scan Photo"}
          </Button>
          {data.holdings && data.holdings.length > 0 && (
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              variant="outline"
              className="rounded-sm border-line text-stone_brand-dark hover:bg-bone-100 h-11 px-5 gap-2"
              data-testid="portfolio-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing…" : "Refresh Prices"}
            </Button>
          )}

          {data.holdings && data.holdings.length > 0 && (
            <>
              <div className="flex items-center gap-1.5" data-testid="portfolio-signal-filter">
                {["ALL", "BUY", "ACCUMULATE", "HOLD", "SELL"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setFilterSignal(s)}
                    className={`px-3 py-1.5 rounded-sm text-[0.65rem] font-mono uppercase tracking-widest transition-colors ${
                      filterSignal === s
                        ? "bg-stone_brand-dark text-bone-50"
                        : "bg-bone-100 border border-line text-stone_brand hover:border-moss/50"
                    }`}
                    data-testid={`filter-${s}`}
                  >
                    {s} {s !== "ALL" && counts[s] !== undefined ? `· ${counts[s]}` : ""}
                  </button>
                ))}
              </div>
              <div className="relative w-full sm:w-64" data-testid="portfolio-search-wrap">
                <Search className="w-4 h-4 text-stone_brand-light absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search ticker or company…"
                  className="h-9 w-full pl-9 pr-8 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss"
                  data-testid="portfolio-search-input"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-stone_brand-light hover:text-terracotta p-1"
                    data-testid="portfolio-search-clear"
                    aria-label="Clear search"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="flex-1" />
              <button
                onClick={handleClearAll}
                className="text-xs font-mono text-stone_brand-light hover:text-terracotta tracking-widest uppercase"
                data-testid="portfolio-clear-all"
              >
                Clear all
              </button>
            </>
          )}
          <p className="basis-full text-[0.7rem] font-mono uppercase tracking-widest text-stone_brand-light">
            — Export your INDmoney holdings from app → Profile → Reports → Holdings Statement (.xlsx) · or snap a photo of your holdings screen
            {data.portfolio_updated_at && (
              <span className="text-moss" data-testid="portfolio-last-synced">
                {" "}· Saved {new Date(data.portfolio_updated_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </p>
        </div>

        {/* Unmatched warning */}
        {unmatched.length > 0 && (
          <div className="bg-amber_brand-bg border border-amber_brand/30 rounded-sm p-4 mb-6 flex items-start gap-3" data-testid="unmatched-warning">
            <AlertCircle className="w-4 h-4 text-amber_brand mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-amber_brand mb-1">
                {unmatched.length} holding{unmatched.length === 1 ? "" : "s"} couldn't be matched to NSE tickers
              </div>
              <div className="text-xs text-stone_brand">
                Likely non-NSE / external holdings: {unmatched.slice(0, 5).join(", ")}
                {unmatched.length > 5 && ` +${unmatched.length - 5} more`}
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="grid gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-14 bg-white border border-line rounded-sm animate-pulse" />
            ))}
          </div>
        ) : rows.length === 0 && data.holdings.length === 0 ? (
          <div className="bg-white border border-line rounded-sm p-12 text-center" data-testid="portfolio-empty">
            <Briefcase className="w-8 h-8 text-stone_brand-light mx-auto mb-4" />
            <div className="font-serif-display text-xl text-stone_brand-dark mb-2">No holdings yet</div>
            <p className="text-sm text-stone_brand mb-5 max-w-md mx-auto">
              In your INDmoney app: Profile → Reports → Holdings Statement → download the .xlsx, then upload it here.
            </p>
            <Button onClick={() => fileRef.current?.click()} className="rounded-sm bg-moss hover:bg-moss-hover text-bone-50 gap-2">
              <Upload className="w-4 h-4" /> Upload your first holdings file
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-line rounded-sm p-12 text-center">
            <div className="font-serif-display text-xl text-stone_brand-dark mb-1">No matches</div>
            <p className="text-sm text-stone_brand">Try loosening the signal filter.</p>
          </div>
        ) : (
          <div className="bg-white border border-line rounded-sm overflow-x-auto" data-testid="portfolio-table">
            <table className="w-full">
              <thead>
                <tr className="border-b border-line text-left bg-white">
                  <SortHeader label="Ticker"        col="ticker"           sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Qty"           col="qty"              sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                  <SortHeader label="Avg Buy"       col="avg_buy_price"    sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                  <SortHeader label="Current"       col="current_price"    sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                  <SortHeader label="Current Value" col="current_value"    sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                  <SortHeader label="P&L %"         col="pnl_pct"          sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric primary />
                  <SortHeader label="M-Star FV"     col="user_morningstar_fv"  sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                  <SortHeader label="2030 Target"   col="user_target_2030" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                  <th className="overline py-4 px-3 text-stone_brand-light whitespace-nowrap">Signal</th>
                  <th className="overline py-4 px-3 text-stone_brand-light whitespace-nowrap"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr
                    key={s.ticker}
                    className="border-b border-line last:border-0 hover:bg-bone-100 transition-colors"
                    data-testid={`portfolio-row-${s.ticker}`}
                  >
                    <td
                      className="py-3 px-3 cursor-pointer"
                      onClick={() => navigate(`/stock/${encodeURIComponent(s.ticker)}`)}
                    >
                      <div className="font-mono font-semibold text-stone_brand-dark">{displayTicker(s.ticker)}</div>
                      <div className="text-xs text-stone_brand truncate max-w-[200px]">{s.company_name}</div>
                    </td>
                    <td className="py-3 px-3 font-mono text-sm text-stone_brand-dark">{s.qty}</td>
                    <td className="py-3 px-3 font-mono text-sm text-stone_brand-dark">{fmtMoney(s.avg_buy_price, "INR")}</td>
                    <td className="py-3 px-3 font-mono text-sm text-stone_brand-dark">{fmtMoney(s.current_price, "INR")}</td>
                    <td className="py-3 px-3 font-mono text-sm text-stone_brand-dark">{fmtMoney(s.current_value, "INR", 0)}</td>
                    <td className={`py-3 px-3 font-mono text-sm font-semibold ${marginColor(s.pnl_pct)}`}>
                      {fmtPct(s.pnl_pct)}
                      {s.pnl !== null && s.pnl !== undefined && (
                        <div className="text-[0.65rem] font-mono opacity-80">{fmtMoney(s.pnl, "INR", 0)}</div>
                      )}
                    </td>
                    <td className="py-3 px-3 font-mono text-sm text-stone_brand-dark">
                      {s.user_morningstar_fv ? fmtMoney(s.user_morningstar_fv, "INR") : <span className="text-stone_brand-light">—</span>}
                    </td>
                    <td className="py-3 px-3 font-mono text-sm text-stone_brand-dark">
                      {s.user_target_2030 ? (
                        <>
                          {fmtMoney(s.user_target_2030, "INR", 0)}
                          {s.user_multiplier_2030 && (
                            <div className="text-[0.6rem] text-stone_brand-light">{s.user_multiplier_2030}</div>
                          )}
                        </>
                      ) : (
                        <span className="text-stone_brand-light">—</span>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <SignalPill rec={s.recommendation} onClick={() => setDetailHolding(s)} />
                    </td>
                    <td className="py-3 px-3 text-right">
                      <button
                        onClick={() => handleRemove(s.ticker)}
                        className="p-1.5 text-stone_brand-light hover:text-terracotta hover:bg-terracotta-bg rounded-sm transition-colors"
                        data-testid={`portfolio-remove-${s.ticker}`}
                        aria-label={`Remove ${s.ticker}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data.holdings && data.holdings.length > 0 && (
          <p className="text-[0.7rem] font-mono uppercase tracking-widest text-stone_brand-light mt-4">
            — Signals = deterministic rules on (current vs Morningstar FV) and (current vs 2030 target). Not investment advice.
          </p>
        )}
      </main>
      {detailHolding && (
        <SignalDetailModal holding={detailHolding} onClose={() => setDetailHolding(null)} />
      )}
    </div>
  );
}

function SortHeader({ label, col, sortBy, sortDir, onClick, numeric, primary }) {
  const active = sortBy === col;
  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      onClick={() => onClick(col)}
      className={`overline py-4 px-3 whitespace-nowrap cursor-pointer select-none transition-colors group ${
        active ? "text-moss" : "text-stone_brand-light hover:text-stone_brand-dark"
      } ${primary ? "bg-moss-bg/30" : ""}`}
      data-testid={`sort-${col}`}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        <Icon className={`w-3 h-3 transition-opacity ${active ? "opacity-100" : "opacity-30 group-hover:opacity-70"}`} />
      </span>
    </th>
  );
}
